import classNames from "classnames";
import classes from "./FreeCapacityPushPage.module.less";
import { resolveResourceURl } from "~/utils/url/urlResolver";
import { Trans, useTranslation } from "react-i18next";
import { Divider } from "~/common/components/Divider";

import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { Stacker } from "~/common/components/Stacker/Stacker";
import {
  Breadcrumbs,
  Collapse,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  ToggleButton,
  ToggleButtonGroup,
} from "~/FreeCapacityPush/components/ToggleButtonGroup/ToggleButtonGroup/ToggleButtonGroup";
import { type FCCState, Hierarchy, Plant, Series, type TreeType, useFCCState } from "~/FreeCapacityPush/useFCCState";
import { MaterialInput, type MaterialInputProps } from "~/common/components/materialInput/MaterialInput";
import debounce from "lodash/debounce";
import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from "react";
import CenterContents from "~/common/components/CenterContents";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { FCCTree, type FCCTreeNode } from "~/FreeCapacityPush/components/FCCTree/FCCTree";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { LinkButton, type LinkButtonProps, LinkType } from "~/common/components/Link";
import { Button } from "~/common/components/Button/Button";
import { SearchResultFCC } from "~/FreeCapacityPush/components/SearchResultFCC/SearchResultFCC";
import Pagination from "~/Search/components/common/Pagination";
import { FreeCapacityTile } from "~/FreeCapacityPush/components/FreeCapacityTile/FreeCapacityTile";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { SalesCoverageTile } from "~/FreeCapacityPush/components/SalesCoverageTile/SalesCoverageTile";
import { NoSearchResults } from "~/Search/components/common/NoSearchResults";
import { AvailabilityTile } from "~/FreeCapacityPush/components/AvailabilityTile/AvailabilityTile";
import { ProductService } from "~/AddToCart/ProductService";
import { getPackageCode } from "~/utils/cartHelper";
import { InfoCircle } from "~/FreeCapacityPush/components/InfoCircle/InfoCircle";
import { FccIntroTooltip } from "~/FreeCapacityPush/components/FccIntroTooltip/FccIntroTooltip";
import { PopperTooltip } from "~/common/components/Tooltip";
import { SortingPicker } from "~/Search/components/common/SortingPicker";
import { Menu, useSimpleMenuState } from "~/common/components/Menu/Menu";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import { downloadFreeCapacityPushSalesCoverage, downloadFreeCapacityPushScope } from "./downloadFreeCapacityPushItems";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { Alert } from "~/common/m3/Alert/Alert";
import { Switch } from "~/common/components/Switch/Switch";

function PageTitle({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <div className={classNames(classes.pageTitle, className)}>
      <h1>{t("page.fcc.title")}</h1>
      <InfoCircle className={classes.pageTitleInfo} content={<FccIntroTooltip />} disableFlip={true} />
    </div>
  );
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const FreeCapacityCockpitPage = () => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();

  const { state, actions } = useFCCState();
  const [isDrawerOpen, setIsDrawerOpen] = useState(() => screenSize === useNumericScreenSize.tablet);
  const [isRenderDrawer, setRenderDrawer] = useState(() => screenSize === useNumericScreenSize.tablet);
  const [showExtraGraphs, setShowExtraGraphs] = useState(false);

  const openDrawer = () => {
    setIsDrawerOpen(true);
    setRenderDrawer(true);
  };

  const selectedProduct = ProductService.useNullableProductDescriptor({
    productCode: state.selectedProductCode ?? "",
    priceReference: "",
  });

  if (screenSize <= useNumericScreenSize.phone) {
    return (
      <div className={classes.tooSmall}>
        <PageTitle />

        <img className={classes.image} src={resolveResourceURl("/images/pcchan.svg")} />

        <p className={classes.text}>{t("page.fcc.tooSmall")}</p>
      </div>
    );
  }

  if (!state.initialised) {
    return (
      <CenterContents className={classes.initialLoading}>
        <CircleSpinner />
      </CenterContents>
    );
  }

  const isDesktop = screenSize >= useNumericScreenSize.desktopS;

  const currentPageProducts = state.results[state.pagination.currentPage] ?? [];
  const showPagination = state.pagination.numberOfPages > 1;
  const showInsightsMessage = !state.selectedTreeNode && !state.selectedProductCode;

  let trainId = undefined;
  let breadcrumbs:
    | {
        code: string;
        name: string;
      }[]
    | undefined = undefined;
  let trainSelected = false;
  if (selectedProduct) {
    const packCode = getPackageCode(state.selectedFifteenDigit!);
    const fifteenDigitData = selectedProduct.base.fifteenDigit[packCode];
    breadcrumbs = fifteenDigitData?.fccBreadcrumb;
    trainId = breadcrumbs?.[breadcrumbs.length - 1]?.name;
    trainSelected = true;
  } else if (state.selectedTree === Plant && state.selectedTreeNode && state.selectedTreeNode.children.length === 0) {
    trainId = state.selectedTreeNode.label;
    breadcrumbs = makeBreadcrumbFromTree(state.filteredData[Plant], state.selectedTreeNode.key);
    trainSelected = true;
  }

  let resultsTitle;
  if (state.loading) {
    resultsTitle = t("searchPage.tabs.productNoCount");
  } else {
    resultsTitle = (
      <Trans i18nKey={"searchPage.tabs.product"} count={state.pagination.totalNumberOfResults}>
        Products (
        <span data-test-id={"results-product-amount"}>{{ count: state.pagination.totalNumberOfResults }}</span>)
      </Trans>
    );
  }

  let productLineLabel = undefined;
  if (state.selectedTree === Hierarchy && state.selectedTreeNode) {
    const productLineKey = "/" + state.selectedTreeNode.key.substring(1).split("/")[0];
    productLineLabel = state.filteredData[Hierarchy].find((node) => node.key === productLineKey)?.label;
  }

  return (
    <>
      <div className={classNames(classes.root)}>
        {/* Left side */}
        {!isDesktop && (
          <div className={classes.tabletHeader}>
            <PageTitle />
            <div className={classes.tabletButtons}>
              {window.SCHA.feature.FCCDownloads && <FreeCapacityPushDownloads />}
              <LinkButton iconBefore={<Icon type={IconType.Filter} />} onClick={openDrawer}>
                {t("notificationsPage.filters.title")}
              </LinkButton>
            </div>
          </div>
        )}
        {isDesktop && (
          <div className={classes.leftSection}>
            <div className={classes.leftSectionInner}>
              <Filters state={state} actions={actions} isDesktop={true} />
            </div>
          </div>
        )}

        {/* Right side */}
        <div className={classes.rightSection}>
          <div className={classes.rightSectionInner}>
            <div className={classes.overviewHeaderContainer}>
              <h2 className={classes.overviewHeader}>{t("page.fcc.overview")}</h2>
              {!isDesktop && (
                <LinkButton
                  color={"primary"}
                  onClick={() => setShowExtraGraphs((prev) => !prev)}
                  iconBefore={<Icon type={showExtraGraphs ? IconType.ChevronUp : IconType.ChevronDown} />}
                >
                  {t("page.fcc.moreGraphsButton")}
                </LinkButton>
              )}
            </div>
            {(breadcrumbs?.length ?? 0) > 0 && (
              <Breadcrumbs maxItems={isDesktop ? 2 : undefined} aria-label="breadcrumb">
                {breadcrumbs?.map(({ code, name }, index) => (
                  <LinkButton
                    key={code}
                    variant={LinkType.SECONDARY}
                    onClick={() => {
                      const node = findTreeNode(state.filteredData[Plant], code);
                      if (node) {
                        actions.selectTreeNode(node, Plant);
                      }
                    }}
                  >
                    {index !== breadcrumbs!.length - 1 ? name : <b>{name}</b>}
                  </LinkButton>
                ))}
              </Breadcrumbs>
            )}
            {showInsightsMessage && <Alert severity={"info"}>{t("page.fcc.noFiltersMessage")}</Alert>}
            <div className={classes.graphs}>
              {state.loadingTrain && (
                <CenterContents>
                  <CircleSpinner />
                </CenterContents>
              )}
              {!state.loadingTrain && (
                <>
                  <FreeCapacityTile data={state.trainDetails} trainLabel={trainId} trainSelected={trainSelected} />
                  <Collapse
                    in={showExtraGraphs || screenSize > useNumericScreenSize.tablet}
                    classes={{
                      wrapperInner: classes.graphCollapse,
                    }}
                  >
                    <AvailabilityTile
                      product={selectedProduct}
                      packageCode={getPackageCode(state.selectedFifteenDigit ?? "")}
                    />
                    <SalesCoverageTile
                      salesCoverage={state.salesCoverage}
                      selectedProductCode={state.selectedProductCode ?? undefined}
                      productLineLabel={productLineLabel}
                      salesCoverageType={state.salesCoverageType}
                      salesCoverageUnit={state.salesCoverageUnit}
                      onSalesCoverageTypeChange={actions.updateSalesCoverageType}
                      onSalesCoverageUnitChange={actions.updateSalesCoverageUnit}
                    />
                  </Collapse>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Center */}
        <div className={classes.middleSection}>
          <div>
            <div id="resultsHeader" className={classes.middleTitle}>
              <div>{resultsTitle}</div>
              {isDesktop && window.SCHA.feature.FCCDownloads && <FreeCapacityPushDownloads />}
            </div>

            <div className={classes.queryRow}>
              <DebouncedInput
                classes={{ root: classes.queryInput }}
                label={t("page.fcc.search.placeholder")}
                onChange={(e) => actions.updateSearchQuery(e.target.value)}
                value={state.searchQuery}
                helperLabel={t("page.fcc.search.helper")}
                disabled={state.loading}
                endAdornment={
                  state.searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton onClick={actions.clearSearchQuery} color={"primary"}>
                        <Icon type={IconType.Close} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    <Icon type={IconType.Search} className={classes.searchIcon} />
                  )
                }
              />

              <SortingPicker
                className={classes.sortingPicker}
                options={state.sorts}
                selectedCode={state.selectedSort}
                selectedDirection={state.selectedSortDirection ?? null}
                onSelect={(newCode) => actions.updateSort(newCode)}
                onAscDesc={(newDirection) => actions.updateSort(state.selectedSort, newDirection)}
                useTranslationKeyForLabel={true}
              />
            </div>

            <Divider className={classes.middleDivider} orientation={"horizontal"} />
          </div>

          <div className={classes.resultsList}>
            {state.loading && (
              <CenterContents>
                <div className={classes.resultsLoading}>
                  <CircleSpinner />
                  <Typography color={"textPrimary"}>{t("page.fcc.loadingResults")}</Typography>
                </div>
              </CenterContents>
            )}
            {!state.loading &&
              currentPageProducts.length > 0 &&
              currentPageProducts.map((productCode) => (
                <SearchResultFCC
                  key={productCode}
                  productCode={productCode}
                  searchQuery={state.searchQuery}
                  isSelected={state.selectedProductCode === productCode}
                  onSelect={(productCode, fifteenDigits) => {
                    actions.selectProduct(productCode, fifteenDigits);
                    setShowExtraGraphs(true);
                  }}
                />
              ))}
            {!state.loading && currentPageProducts.length === 0 && <NoSearchResults mode={"short"} />}
          </div>

          {!state.loading && showPagination && (
            <Pagination
              classes={{ root: "scha__srp__pagination" }}
              pageAmount={state.pagination.numberOfPages}
              currentPage={state.pagination.currentPage}
              onPageChange={actions.changePage}
              renderHrefs={true}
              goToIdAfterChange={"#resultsHeader"}
            />
          )}
        </div>
      </div>

      {!isDesktop && isRenderDrawer && (
        <SettingsDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          initialState={state}
          onClear={actions.resetState}
          onSubmit={actions.updateState}
          onExited={() => setRenderDrawer(false)}
        />
      )}
    </>
  );
};

const DebouncedInput = ({ value, onChange, ...rest }: MaterialInputProps) => {
  const debouncedOnChange = useMemo(() => {
    return debounce(onChange ?? (() => {}), 500);
  }, [onChange]);
  const [internalValue, setInternalValue] = useState(value);
  useLayoutEffect(() => {
    setInternalValue(value);
  }, [value]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    debouncedOnChange(e);
  };
  return <MaterialInput value={internalValue} onChange={handleChange} {...rest} />;
};

const AccountInformation = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const name = window.SCHA.user.selectedAccount?.name ?? "N/A";
  const accountNumber = window.SCHA.user.selectedAccount?.displayUid ?? "N/A";
  return (
    <div className={classNames(classes.accountInfo, className)}>
      <span className={classes.accountInfoLabel}>{t("page.fcc.account")}</span>
      <AccountName className={classes.accountName} name={name} />
      <Divider orientation={"vertical"} flexItem />
      <span>{accountNumber}</span>
    </div>
  );
};

const AccountName = ({ name, className }: { name: string; className?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [addTooltip, setAddTooltip] = useState(false);
  useLayoutEffect(() => {
    if (ref.current) {
      setAddTooltip(ref.current.offsetWidth < ref.current.scrollWidth);
    }
  }, [name]);
  let el = (
    <span ref={ref} className={className}>
      {name}
    </span>
  );
  if (addTooltip) {
    el = (
      <PopperTooltip tooltipContent={name} className={className}>
        {el}
      </PopperTooltip>
    );
  }
  return el;
};

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  initialState: FCCState;
  onClear: () => void;
  onSubmit: (updatedSettings: FCCState) => void;
  onExited?: () => void;
}

const SettingsDrawer = ({ initialState, open, onClose, onClear, onSubmit, onExited }: SettingsDrawerProps) => {
  const { t } = useTranslation();
  const { state, actions } = useFCCState(true);
  useLayoutEffect(() => actions.updateState(initialState), [initialState]);
  return (
    <Drawer anchor={"right"} open={open} onClose={onClose} ModalProps={{ onTransitionExited: onExited }}>
      <section className={classes.drawerContents}>
        <header className={classes.drawerHeader}>
          <div className={classes.drawerTitle}>
            <p>{t("notificationsPage.settings.filter")}</p>
            <IconButton onClick={onClose} color={"primary"} className={classes.drawerClose}>
              <Icon type={IconType.Close} />
            </IconButton>
          </div>
          <Divider orientation={"horizontal"} />
        </header>
        <Filters state={state} actions={actions} isDesktop={false} />
        <footer className={classes.drawerButtons}>
          <Button
            size={"medium"}
            disabled={state.loading}
            onClick={() => {
              onClear();
              onClose();
            }}
            variant={"text"}
          >
            {t("page.linear.links.reset")}
          </Button>
          <Button
            disabled={state.loading}
            size={"medium"}
            onClick={() => {
              onSubmit(state);
              onClose();
            }}
          >
            {t("regionSelectionBanner.apply")}
          </Button>
        </footer>
      </section>
    </Drawer>
  );
};

const TREE_FILTER_LABELS: Record<TreeType, string> = {
  [Series]: "page.fcc.search.productSeries",
  [Hierarchy]: "page.fcc.search.productHierarchy",
  [Plant]: "page.fcc.search.plant",
};

const Filters = ({ state, actions, isDesktop }: ReturnType<typeof useFCCState> & { isDesktop: boolean }) => {
  const { t } = useTranslation();

  const treeData = state.filteredData[state.visibleTree];
  const isTreeEmpty = treeData.length === 0;

  const treeProps = {
    items: treeData,
    expanded: state.expanded[state.visibleTree],
    selected: state.selectedTreeNode?.key ?? null,
    setSelected: (node: FCCTreeNode | null) => actions.selectTreeNode(node, state.visibleTree),
    setExpanded: actions.setExpanded,
    onReset: isDesktop ? actions.resetTree : undefined,
  };

  const isNewSearchLoading = state.loading && state.pagination.currentPage === 0;

  const treeFilterLabel = t(TREE_FILTER_LABELS[state.visibleTree]);

  return (
    <Stacker gap={24}>
      <Stacker gap={16}>
        {isDesktop && <PageTitle />}
        {isDesktop && <Divider flexItem orientation={"horizontal"} />}
        <AccountInformation />
        <Stacker gap={8} direction={"row"} className={classes.stacker}>
          <Switch
            checked={state.showOnlyRelevantProducts}
            onChange={(_, value) => actions.toggleShowOnlyRelevantProducts(value)}
          />
          <span>{t("page.fcc.onlyRelevant.title")}</span>
          <InfoCircle
            content={
              <Typography className={"scha__popper-tooltip__inner"}>
                {t("page.fcc.onlyRelevant.description")}
              </Typography>
            }
          />
        </Stacker>
      </Stacker>
      <FormControl>
        <FormLabel className={classes.formLabel}>{t("page.fcc.view")}</FormLabel>
        <ToggleButtonGroup
          value={state.visibleTree}
          exclusive
          onChange={(_, value) => {
            if (!value) {
              return;
            }
            actions.changeVisibleTree(value as TreeType);
          }}
          fullWidth={true}
        >
          <ToggleButton value={Series}>{t("page.fcc.tree.series")}</ToggleButton>
          <ToggleButton value={Hierarchy}>{t("page.fcc.tree.hierarchy")}</ToggleButton>
          <ToggleButton value={Plant}>{t("page.fcc.tree.plant")}</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      <div>
        <DebouncedInput
          label={treeFilterLabel}
          onChange={(e) => actions.updateTreeFilterQuery(e.target.value)}
          value={state.treeFilterQuery}
          disabled={state.loading}
          endAdornment={
            state.treeFilterQuery ? (
              <InputAdornment position="end">
                <IconButton onClick={actions.clearTreeFilterQuery} color={"primary"}>
                  <Icon type={IconType.Close} />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }
        />
        {isNewSearchLoading && (
          <CenterContents>
            <CircleSpinner />
          </CenterContents>
        )}
        {!isNewSearchLoading && isTreeEmpty && <span className={"title-large"}>{t("page.fcc.tree.noResults")}</span>}
        {!isNewSearchLoading && !isTreeEmpty && <FCCTree {...treeProps} searchTerm={state.treeFilterQuery} />}
      </div>
    </Stacker>
  );
};

const DownloadClickLinkWithLoading = forwardRef<
  HTMLButtonElement,
  Omit<LinkButtonProps, "iconBefore"> & { loading?: boolean }
>((props, ref) => (
  <LinkButton
    disabled={props.loading}
    ref={ref}
    iconBefore={props.loading ? <CircleSpinner className={classes.spinner} /> : <Icon type={IconType.Download} />}
    {...props}
  />
));

const FreeCapacityPushDownloads = () => {
  const { t } = useTranslation();
  const [isOpen, handleClick, handleClose, anchorEl] = useSimpleMenuState();
  const { val: loading, setTrue: startLoading, setFalse: endLoading } = useBooleanState(false);
  return (
    <div>
      <div className={classNames(classes.downloadContainer, classes.downloadText)}>
        <span className={classes.downloadLabel}>{`${t(
          "productDetailsPage.tabs.downloads.tiles.button.download",
        )}:`}</span>
        <DownloadClickLinkWithLoading loading={loading} className="title-small" onClick={handleClick}>
          {t("downloadFile.type.csv")}
        </DownloadClickLinkWithLoading>
      </div>

      <Menu
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={isOpen && !loading}
        onClose={handleClose}
        anchorEl={anchorEl}
      >
        <MenuItem
          onClick={async () => {
            startLoading();
            await downloadFreeCapacityPushScope();
            endLoading();
          }}
        >
          {t("quote.table.header.fcc.scope.title")}
        </MenuItem>
        <MenuItem
          onClick={async () => {
            startLoading();
            await downloadFreeCapacityPushSalesCoverage();
            endLoading();
          }}
        >
          {t("page.fcc.salesCoverage.download")}
        </MenuItem>
      </Menu>
    </div>
  );
};

/**
 * Create a breadcrumb from a tree and a selected node. The node can be at any level of the tree.
 * @param tree
 * @param selected
 */
function makeBreadcrumbFromTree(
  tree: FCCTreeNode[],
  selected: string | null,
): {
  code: string;
  name: string;
}[] {
  if (!selected) {
    return [];
  }
  for (const node of tree) {
    if (node.key === selected) {
      return [
        {
          code: node.key,
          name: node.label,
        },
      ];
    }
    const children = makeBreadcrumbFromTree(node.children, selected);
    if (children.length > 0) {
      return [
        {
          code: node.key,
          name: node.label,
        },
        ...children,
      ];
    }
  }
  return [];
}

function findTreeNode(tree: FCCTreeNode[], code: string): FCCTreeNode | null {
  for (const node of tree) {
    if (node.key === code) {
      return node;
    }
    const child = findTreeNode(node.children, code);
    if (child) {
      return child;
    }
  }
  return null;
}
